import { Box, Center, Group, Overlay, Skeleton, rem, useMantineTheme } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { useFileManager } from 'api/file-manager/file-manager-context';
import { FEATURE_TOGGLE_DEVICE_IMAGES_SLIDER } from 'env';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { DefaultExtensionType, FileIcon, defaultStyles } from 'react-file-icon';

/**
 * Parameters of the FileThumbnail component.
 */
export interface FileThumbnailProps {
  fileId: string;
  size?: 'sm' | 'md' | 'lg';
  showSlider?: boolean;
  onOpenSlider?: () => void;
  onUnsupportedPreviewClick?: () => void;
}

const FILE_THUMBNAIL_SIZE_CONFIG = {
  sm: { frameSize: 56, imagePreviewHeight: 36, iconSize: 32 },
  md: { frameSize: 80, imagePreviewHeight: 44, iconSize: 48 },
  lg: { frameSize: 104, imagePreviewHeight: 60, iconSize: 64 },
} as const;

/**
 * Displays a thumbnail for the given file extension.
 */
export default function FileThumbnail({
  fileId,
  size = 'md',
  showSlider = true,
  onOpenSlider = noop,
  onUnsupportedPreviewClick,
}: FileThumbnailProps) {
  const theme = useMantineTheme();

  const { getFileMetadata, getFileThumbnail } = useFileManager();
  const [loading, setLoading] = useState(true);
  const [extension, setExtension] = useState('');
  const [contents, setContents] = useState('');
  const [canPreview, setCanPreview] = useState(false);
  const { frameSize, imagePreviewHeight, iconSize } = FILE_THUMBNAIL_SIZE_CONFIG[size];

  useEffect(() => {
    (async () => {
      try {
        const metadata = await getFileMetadata({ fileId });

        const extension = (metadata.extension as string).replace(/^\./, '');

        const isImage = metadata.mimeType.startsWith('image/');
        const isPdf = metadata.mimeType === 'application/pdf';

        setExtension(extension.toLowerCase());
        setCanPreview(isImage || isPdf);

        if (isImage) {
          const contents = await getFileThumbnail({ fileId, size: '96x96' });
          setContents(URL.createObjectURL(contents));
        }
      } catch (error: any) {
        panic(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <Skeleton w="100%" h="100%" radius={8} />;
  }

  if (contents) {
    return FEATURE_TOGGLE_DEVICE_IMAGES_SLIDER && showSlider ? (
      <Box className="relative flex items-center justify-center" sx={{ borderRadius: '8px' }}>
        <Group
          h={imagePreviewHeight}
          w="100%"
          className="group/file-thumbnail"
          sx={{ borderRadius: '8px', border: `2px solid ${theme.colors.gray[1]}` }}
        >
          <Box
            component="img"
            p={4}
            className="h-full w-full object-cover"
            w="100%"
            h={imagePreviewHeight}
            src={contents}
            alt={extension}
            sx={{ borderRadius: '8px' }}
          />
          <Overlay
            color="#000"
            zIndex={5}
            opacity={0.4}
            className="hidden cursor-pointer items-center justify-center rounded-[8px] group-hover/file-thumbnail:flex"
            onClick={onOpenSlider}
          >
            <IconEye width={20} height={20} color="#FFF" stroke={1.5} />
          </Overlay>
        </Group>
      </Box>
    ) : (
      <Box component="img" h={frameSize} p={4} w="100%" src={contents} alt={extension} sx={{ borderRadius: '8px' }} />
    );
  }

  const fileThumbnail = (
    <Box h={iconSize} w={iconSize} sx={{ svg: { width: rem(iconSize), height: rem(iconSize) } }}>
      <FileIcon extension={extension} {...(defaultStyles[extension as DefaultExtensionType] || {})} />
    </Box>
  );

  if (canPreview && FEATURE_TOGGLE_DEVICE_IMAGES_SLIDER && showSlider) {
    return (
      <Center
        className="group/file-thumbnail relative flex items-center justify-center"
        bg="#FFF"
        m={4}
        sx={{ borderRadius: '4px' }}
        h="100%"
      >
        {fileThumbnail}
        <Overlay
          color="#000"
          zIndex={5}
          opacity={0.4}
          className="hidden cursor-pointer items-center justify-center rounded-[8px] group-hover/file-thumbnail:flex"
          onClick={onOpenSlider}
        >
          <IconEye width={20} height={20} color="#FFF" stroke={1.5} />
        </Overlay>
      </Center>
    );
  }

  return (
    <Center
      h="100%"
      p={4}
      sx={{ borderRadius: '4px' }}
      style={{ cursor: onUnsupportedPreviewClick ? 'pointer' : undefined }}
      onClick={onUnsupportedPreviewClick}
    >
      <Center h="100%" bg="#fff" sx={{ borderRadius: '4px' }}>
        {fileThumbnail}
      </Center>
    </Center>
  );
}
